import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Modal } from '@safetyhub/ui';
import { formatAnnexLabel } from '@safetyhub/shared';
import { adminApi, extractApiError } from '../../lib/api';

/** 고시 별표 고르기가 돌려주는 근거 키 — 카탈로그 조건 줄의 `ADMIN_RULE_ANNEX` 근거(`sourceAdminRuleId`·`sourceAnnexNumber`·`sourceAnnexSubNumber`) */
export interface AdminRuleAnnexPick {
  ruleId: string;
  ruleName: string;
  annexNumber: string;
  annexSubNumber: string;
  title: string;
}

interface Props {
  onClose: () => void;
  onPick: (pick: AdminRuleAnnexPick) => void;
  /** 모달 층 — 여는 쪽이 준다(다른 모달 위에서 열린다) */
  overlayZClassName?: string;
  overlayBgClassName?: string;
}

/**
 * **고시 별표 고르기** — 카탈로그 조건 줄의 근거(종류 「고시 별표」)를 정하는 자리(W3 2026-11-04 · 설계 §6 W3).
 *
 * 재료는 기존 읽기 라우트 둘이다(새 서버 엔드포인트 없음): `GET /admin/admin-rules`(버전 목록 — **현행**(`isCurrent`)이고 행정규칙ID가 있는 것만
 * 고른다) → `GET /admin/admin-rules/:id`(그 버전의 별표). 서버의 근거 실재 판정(`conditionSourceExistenceProblems`)도 현행 고시에서 보므로 고르는
 * 분모와 판정 분모가 같다. 상세 응답은 별표 원문을 함께 싣는다 — 고를 때 읽을 수 있게 그대로 보인다.
 * 「서식」은 고르지 않는다(근거 실재가 「별표」만 센다 — 법령 쪽 `LawAnnexesModal`의 `kind: '별표'`와 같은 축).
 * ⚠️ 이 모달은 아무것도 쓰지 않는다 — 저장은 부른 쪽(조건 PATCH)이 하고 서버가 실재를 다시 본다.
 */
export default function AdminRuleAnnexPickerModal({ onClose, onPick, overlayZClassName, overlayBgClassName }: Props) {
  const [ruleDbId, setRuleDbId] = useState<string>('');
  const [annexId, setAnnexId] = useState<string | null>(null);

  const rules = useQuery({
    queryKey: ['admin', 'admin-rules', 'list'],
    queryFn: async () => {
      const res = await adminApi.getAdminRules();
      if (!res.data) throw new Error('서버 응답에 본문이 없습니다. 잠시 후 다시 시도해 주세요.');
      return res.data.items;
    },
  });
  const currentRules = useMemo(() => (rules.data ?? []).filter((r) => r.isCurrent && r.ruleId), [rules.data]);
  const selectedRuleId = ruleDbId || currentRules[0]?.id || '';

  const detail = useQuery({
    queryKey: ['admin', 'admin-rules', 'detail', selectedRuleId],
    queryFn: async () => {
      const res = await adminApi.getAdminRule(selectedRuleId);
      if (!res.data) throw new Error('서버 응답에 본문이 없습니다. 잠시 후 다시 시도해 주세요.');
      return res.data;
    },
    enabled: selectedRuleId !== '',
  });
  const annexes = (detail.data?.annexes ?? []).filter((a) => a.annexKind === '별표');
  const selected = annexes.find((a) => a.id === annexId) ?? annexes[0] ?? null;

  return (
    <Modal
      open
      onClose={onClose}
      title="고시 별표 — 근거로 지정할 별표 고르기"
      size="xl"
      overlayZClassName={overlayZClassName}
      overlayBgClassName={overlayBgClassName}
      footer={
        <Button variant="secondary" onClick={onClose}>
          닫기
        </Button>
      }
    >
      {rules.isLoading ? (
        <div className="py-6 text-sm text-gray-500">불러오는 중...</div>
      ) : rules.error ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
          고시 목록을 불러오지 못했습니다. {extractApiError(rules.error).message}
        </div>
      ) : currentRules.length === 0 ? (
        <div className="py-6 text-sm text-gray-500">수집된 현행 고시가 없습니다 — 고시 수집이 돌기 전이거나 현행 판정 전입니다.</div>
      ) : (
        <div className="space-y-4">
          <div className="w-96">
            <label htmlFor="admin-rule-annex-rule" className="block text-sm font-medium text-gray-700 mb-1">
              고시(현행)
            </label>
            <select
              id="admin-rule-annex-rule"
              value={selectedRuleId}
              onChange={(e) => {
                setRuleDbId(e.target.value);
                setAnnexId(null);
              }}
              className="w-full px-3 py-2 border rounded-control"
            >
              {currentRules.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.enforcementDate ? ` (시행 ${r.enforcementDate})` : ''}
                </option>
              ))}
            </select>
          </div>
          {detail.isLoading ? (
            <div className="py-6 text-sm text-gray-500">불러오는 중...</div>
          ) : detail.error ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
              별표를 불러오지 못했습니다. {extractApiError(detail.error).message}
            </div>
          ) : annexes.length === 0 ? (
            <div className="py-6 text-sm text-gray-500">이 고시에 수집된 별표가 없습니다.</div>
          ) : (
            <div className="grid grid-cols-[16rem_1fr] gap-4 min-h-[20rem]">
              <ul aria-label="고시 별표 목록" className="divide-y divide-gray-100 border rounded-card overflow-y-auto max-h-[60vh]">
                {annexes.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      aria-pressed={selected?.id === a.id}
                      onClick={() => setAnnexId(a.id)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                        selected?.id === a.id ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-800'
                      }`}
                    >
                      <span className="block">{formatAnnexLabel(a.annexKind, a.annexNumber, a.annexSubNumber)}</span>
                      <span className="block text-xs text-gray-500 truncate">{a.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {selected && detail.data && (
                <section aria-label={formatAnnexLabel(selected.annexKind, selected.annexNumber, selected.annexSubNumber)}>
                  <h3 className="text-sm font-bold text-gray-900 mb-2">
                    {`${formatAnnexLabel(selected.annexKind, selected.annexNumber, selected.annexSubNumber)} — ${selected.title}`}
                  </h3>
                  <Button
                    size="sm"
                    className="mb-2"
                    onClick={() => {
                      const rule = detail.data.rule;
                      if (!rule.ruleId) return;
                      onPick({
                        ruleId: rule.ruleId,
                        ruleName: rule.name,
                        annexNumber: selected.annexNumber,
                        annexSubNumber: selected.annexSubNumber,
                        title: selected.title,
                      });
                    }}
                  >
                    이 별표를 근거로 지정
                  </Button>
                  {/* 괘선 표 — 줄바꿈·칸 공백이 구조라 whitespace-pre + 고정폭(형제 `LawAnnexesModal`과 같은 이유) */}
                  <pre className="p-3 bg-gray-50 rounded-card text-xs font-mono whitespace-pre overflow-auto max-h-[50vh]">
                    {selected.content}
                  </pre>
                </section>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
